import type { Bid, ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import Datetime from 'utils/datetime/datetime'
import { serializeDecimalInputText } from 'utils/numberUtils/decimalInput'
import { getDayAheadBidCellKey, makeDayAheadBidHourRowLookup } from './DayAheadBidTable.grid.helper'
import type {
  DayAheadBidColumn,
  DayAheadBidRow,
  SeededDayAheadBidGrid,
  SkippedDayAheadBid,
} from './DayAheadBidTable.types'

function toBidCellValue(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : serializeDecimalInputText(value)
}

function resolveBidVolumeByHour(volume: number[] | null, hourCount: number): number[] | null {
  if (volume === null) {
    return null
  }

  if (volume.length === 1) {
    return Array.from({ length: hourCount }, () => volume[0])
  }

  return volume.length === hourCount ? volume : null
}

type SeedDayAheadBidGridInput = {
  bids: Bid[]
  rows: DayAheadBidRow[]
  columns: DayAheadBidColumn[]
  configuredTagsForDate?: ElplanInfoTagForDate[]
  tagsForDate?: ElplanInfoTagForDate[]
}

export function seedDayAheadBidGrid({
  bids,
  rows,
  columns,
  configuredTagsForDate,
  tagsForDate,
}: SeedDayAheadBidGridInput): SeededDayAheadBidGrid {
  const valuesByRow: RowValues[] = rows.map(() => ({}))
  const skipped: SkippedDayAheadBid[] = []
  const columnTagIds = new Set(columns.map((column) => column.tagId))
  const configuredTagIds = new Set((configuredTagsForDate ?? tagsForDate ?? []).map((tag) => tag.id))
  const seededTagIds = new Set<number>()
  const priceRowIndex = rows.findIndex((row) => row.kind === 'price')
  const nameRowIndex = rows.findIndex((row) => row.kind === 'name')
  const firstHourRow = rows.find((row) => row.kind === 'hour')
  const tableDate = firstHourRow ? Datetime.toLocalTime(firstHourRow.startTime, 'onlyDate') : undefined
  const findHourRowIndex = makeDayAheadBidHourRowLookup(rows)

  for (const bid of bids) {
    if (bid.type !== 'blockbid' && bid.type !== 'profilebid') {
      continue
    }

    if (tableDate && Datetime.toLocalTime(bid.start_time, 'onlyDate') !== tableDate) {
      continue
    }

    const skip = (reason: SkippedDayAheadBid['reason']): void => {
      skipped.push({ bidId: bid.id, name: bid.name, reason })
    }

    const tagId = bid.tag
    if (tagId === null || tagId === undefined) {
      skip('no-tag')
      continue
    }

    if (!columnTagIds.has(tagId)) {
      if (!configuredTagIds.has(tagId)) {
        skip('unknown-tag')
      }
      continue
    }

    if (Datetime.isAfter(bid.start_time, bid.end_time)) {
      skip('invalid-span')
      continue
    }

    if (seededTagIds.has(tagId)) {
      skip('duplicate-tag')
      continue
    }

    if (nameRowIndex >= 0) {
      valuesByRow[nameRowIndex][getDayAheadBidCellKey(nameRowIndex, tagId)] = bid.name
    }

    const bidHours = Datetime.getHoursBetween(bid.start_time, bid.end_time)
    const volumeByHour = resolveBidVolumeByHour(bid.volume, bidHours.length)
    if (!volumeByHour) {
      skip('volume-unusable')
      continue
    }

    const placements = bidHours.flatMap((hourStart, hourIndex) => {
      const rowIndex = findHourRowIndex(hourStart)
      return rowIndex === undefined ? [] : [{ rowIndex, volume: volumeByHour[hourIndex] }]
    })

    if (placements.length === 0) {
      skip('no-overlap')
      continue
    }

    for (const { rowIndex, volume } of placements) {
      valuesByRow[rowIndex][getDayAheadBidCellKey(rowIndex, tagId)] = toBidCellValue(volume)
    }

    if (priceRowIndex >= 0) {
      valuesByRow[priceRowIndex][getDayAheadBidCellKey(priceRowIndex, tagId)] = toBidCellValue(bid.props.price)
    }

    seededTagIds.add(tagId)
  }

  return { valuesByRow, skipped }
}
